import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import {
  AlertTriangle,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Layers,
  Plus,
  QrCode,
  Trash2,
} from 'lucide-react'
import { Button } from '@/app/components/ui/Button'
import { Surface } from '@/app/components/ui/Surface'
import { getLayout, layoutBackground, layoutOverlay, saveLayout } from '@/app/lib/local/layouts'
import { cn } from '@/app/lib/cn'
import { generateLayoutPreview } from '@/core/preview'
import {
  QR_DEFAULTS,
  SLOT_DEFAULTS,
  fitScale,
  isQrWithin,
  isSlotWithin,
  moveSlot,
  nudgeForKey,
  snapToCentre,
  snapTolerance,
  previewBox,
  resizeQr,
  resizeSlot,
  recomputeSlot,
  slotRect,
  toQrConfig,
  toSlotConfig,
  toSlotDraft,
  type SlotDraft,
} from '@/core/layout'
import type { LayoutConfig, SlotOrientation, SlotRatio } from '@/core/types'

/** Moving a slot, or resizing it from its corner grip. */
type DragMode = 'move' | 'resize'

/**
 * The layout designer (CreateLayoutWindow.xaml).
 *
 * The MATHS is the desktop app's and is frozen (§5): everything lives in
 * background-image pixel space, the height box stays read-only and derived, and
 * the Portrait/Landscape width-and-height swap happens on save. All of that is
 * in `@/core/layout` and none of it changed here.
 *
 * The SCREEN is not frozen (§5a), and this is the one that most needed the
 * licence. The ported version was a spreadsheet: two number boxes per slot, a
 * canvas pinned at a fixed 0.5 scale, and no way to see which slot you were
 * editing. What changed, and why:
 *
 *  - **The canvas fits the viewport.** 0.5 was fine for the 1620x1080 frames the
 *    desktop designer was built around, but a 4R background at 600 DPI is
 *    2400x3600 — half of that is taller than a laptop screen, and because the
 *    canvas was centred inside an `overflow-auto` box, the slots that landed
 *    off the top and left could not be scrolled to at all. `fitScale` sizes it
 *    to the space available instead.
 *  - **Slots are dragged and resized.** Typing coordinates to position a photo
 *    on a frame you are looking at is the wrong instrument. The number boxes
 *    stay, because they are how you match a frame's exact geometry, and
 *    dragging writes the same integers. The corner grip is aspect-LOCKED: only
 *    the width box can be set, because the ratio derives the height (§5), so
 *    pulling down on a Landscape slot widens it — down is its width box.
 *  - **Arrow keys nudge.** A drag cannot express the difference between x=60 and
 *    x=62 when one screen pixel is five background pixels, and that is exactly
 *    the correction a frame needs once it is roughly placed. 1px, or 10 with
 *    Shift, in background pixels so the canvas and the number boxes agree.
 *  - **Slots can be reordered.** Photo *i* goes to slot *i* — that pairing is
 *    the whole reason a print comes out the way it does — and the ported
 *    designer could only append. Getting the order wrong meant deleting every
 *    slot after the mistake.
 *  - **An overlay layer.** The background goes under the photos; an overlay goes
 *    over them, for a frame whose edges overlap the slots. On this canvas it is
 *    drawn in the SAME order the compositor uses — over every photo slot, under
 *    the QR — so the canvas is a preview rather than an approximation. It is
 *    pointer-transparent, which is what keeps the slots underneath draggable
 *    through it; the eye toggle is for when an opaque overlay hides the slot
 *    being placed.
 *  - **Dragging snaps to the centre lines.** Centring a slot by eye is the one
 *    placement a drag cannot otherwise make — the number boxes could, but only
 *    after working out (2400 - 900) / 2, which is the arithmetic this screen
 *    exists to avoid. Each axis snaps independently, and arrow nudges never
 *    snap, because those are the precise gesture.
 *  - **Off-background slots are called out.** The compositor draws where it is
 *    told, so a slot pushed past the edge is simply missing from the print with
 *    nothing to explain it.
 *
 * Unlike the WPF designer this can also reopen an existing layout — its
 * "Edit Layout" button was present but Collapsed and had no handler.
 */
export function LayoutDesignerScreen() {
  const navigate = useNavigate()
  const { layoutId } = useParams()

  const [name, setName] = useState('')
  const [background, setBackground] = useState<Blob | null>(null)
  const [backgroundName, setBackgroundName] = useState('')
  const [backgroundUrl, setBackgroundUrl] = useState('')
  const [overlay, setOverlay] = useState<Blob | null>(null)
  const [overlayName, setOverlayName] = useState('')
  const [overlayUrl, setOverlayUrl] = useState('')
  /** Overlays cover the slots by design, so the canvas can hide it while editing. */
  const [showOverlay, setShowOverlay] = useState(true)
  const [size, setSize] = useState<{ width: number; height: number } | null>(null)
  const [slots, setSlots] = useState<SlotDraft[]>([])
  const [qr, setQr] = useState<{ x: number; y: number; width: number } | null>(null)
  const [selected, setSelected] = useState<number | 'qr' | null>(null)
  /** Centre guides, shown only while a drag is actually snapped to one. */
  const [guides, setGuides] = useState({ x: false, y: false })
  const [saving, setSaving] = useState(false)

  // ── loading an existing layout ─────────────────────────────────────────────

  useEffect(() => {
    if (!layoutId) return

    let cancelled = false
    void (async () => {
      const existing = await getLayout(layoutId)
      if (!existing || cancelled) return

      setName(existing.name ?? '')
      setBackgroundName(existing.backgroun_path)
      setSlots(existing.slots.map(toSlotDraft))
      setQr(existing.qr ? { x: existing.qr.x, y: existing.qr.y, width: existing.qr.width } : null)

      setOverlayName(existing.overlay_path ?? '')

      const blob = await layoutBackground(existing)
      if (blob && !cancelled) setBackground(blob)

      const over = await layoutOverlay(existing)
      if (over && !cancelled) setOverlay(over)
    })()

    return () => {
      cancelled = true
    }
  }, [layoutId])

  // One object URL per chosen background, released when it is replaced — the
  // designer is reopened repeatedly while a layout is being tuned.
  useEffect(() => {
    if (!background) {
      setBackgroundUrl('')
      setSize(null)
      return
    }

    const url = URL.createObjectURL(background)
    setBackgroundUrl(url)

    let cancelled = false
    void (async () => {
      try {
        const bitmap = await createImageBitmap(background)
        if (!cancelled) setSize({ width: bitmap.width, height: bitmap.height })
        bitmap.close()
      } catch {
        toast.error('Gambar latar tidak bisa dibaca')
      }
    })()

    return () => {
      cancelled = true
      URL.revokeObjectURL(url)
    }
  }, [background])

  // The overlay needs no size of its own — it is stretched to the background —
  // so this only has to keep an object URL alive for the canvas.
  useEffect(() => {
    if (!overlay) {
      setOverlayUrl('')
      return
    }
    const url = URL.createObjectURL(overlay)
    setOverlayUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [overlay])

  // ── canvas scale ───────────────────────────────────────────────────────────

  const viewportRef = useRef<HTMLDivElement>(null)
  const [viewport, setViewport] = useState({ width: 0, height: 0 })

  useLayoutEffect(() => {
    const element = viewportRef.current
    if (!element) return

    const observer = new ResizeObserver(([entry]) => {
      const box = entry?.contentRect
      if (box) setViewport({ width: box.width, height: box.height })
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // A little padding so the frame does not touch the panel edge.
  const scale = size
    ? fitScale(size.width, size.height, viewport.width - 24, viewport.height - 24)
    : 0.5

  // ── editing ────────────────────────────────────────────────────────────────

  const patchSlot = (index: number, patch: Partial<SlotDraft>, cause: 'width' | 'other') =>
    setSlots((current) =>
      current.map((slot, i) => (i === index ? recomputeSlot({ ...slot, ...patch }, cause) : slot)),
    )

  const addSlot = () => {
    setSlots((current) => [...current, { ...SLOT_DEFAULTS }])
    setSelected(slots.length)
  }

  const removeSlot = (index: number) => {
    setSlots((current) => current.filter((_, i) => i !== index))
    setSelected(null)
  }

  /**
   * Moves a slot within the order. That order IS the photo pairing — photo 1
   * lands in slot 1 — so this is geometry-neutral and meaning-heavy.
   */
  const reorderSlot = (index: number, direction: -1 | 1) => {
    const target = index + direction
    setSlots((current) => {
      if (target < 0 || target >= current.length) return current
      const next = [...current]
      const [moved] = next.splice(index, 1)
      next.splice(target, 0, moved!)
      return next
    })
    setSelected(target)
  }

  /**
   * Picks an image for either layer.
   *
   * The overlay wants a transparent PNG — it is drawn over the photos, so a JPEG
   * would simply hide them — but the picker does not enforce that: a fully
   * opaque overlay is a legitimate way to make a frame that covers everything
   * but the slots, and refusing it would be guessing at intent.
   */
  const chooseImage = async (layer: 'background' | 'overlay') => {
    try {
      if (!('showOpenFilePicker' in window)) {
        throw new Error('Browser ini tidak mendukung pemilihan berkas')
      }

      const [handle] = await window.showOpenFilePicker({
        id: `citaria-layout-${layer}`,
        types: [{ description: 'Gambar', accept: { 'image/*': ['.png', '.jpg', '.jpeg'] } }],
        excludeAcceptAllOption: true,
        multiple: false,
      })
      if (!handle) return

      const file = await handle.getFile()
      if (layer === 'background') {
        setBackground(file)
        setBackgroundName(file.name)
      } else {
        setOverlay(file)
        setOverlayName(file.name)
        setShowOverlay(true)
      }
    } catch (error) {
      // A cancelled picker is an outcome, not a failure.
      if (error instanceof DOMException && error.name === 'AbortError') return
      toast.error(error instanceof Error ? error.message : 'Tidak bisa membuka dialog')
    }
  }

  const chooseBackground = () => chooseImage('background')

  // ── dragging on the canvas ─────────────────────────────────────────────────

  /**
   * Drags a slot or the QR box.
   *
   * Deltas are converted from screen pixels back into background pixels by the
   * current scale, so what is stored is exactly what the number boxes show — the
   * canvas is an input method, not a second source of truth.
   */
  const startDrag = (event: React.PointerEvent, index: number | 'qr', mode: DragMode = 'move') => {
    if (!size) return
    event.preventDefault()
    // A resize handle sits inside the slot, so without this the slot would also
    // start moving under the same press.
    event.stopPropagation()
    setSelected(index)

    const startX = event.clientX
    const startY = event.clientY
    const origin =
      index === 'qr'
        ? { x: qr?.x ?? 0, y: qr?.y ?? 0 }
        : { x: slots[index]?.x ?? 0, y: slots[index]?.y ?? 0 }
    // For a resize, the rect being dragged is the one under the pointer at
    // press time; deltas are added to it rather than to the slot's origin.
    const startRect =
      index === 'qr'
        ? { width: qr?.width ?? 0, height: qr?.width ?? 0 }
        : slotRect(slots[index] ?? SLOT_DEFAULTS)

    const target = event.currentTarget as HTMLElement

    // `preventDefault` above suppresses the compatibility mousedown, and with it
    // the browser's own click-to-focus — so a clicked slot stayed unfocused and
    // the arrow keys went to the document instead. Focus it by hand.
    //
    // Not `target`: for a resize that is the corner grip, which is tabIndex -1.
    // The marker attribute finds the slot itself either way.
    target.closest<HTMLElement>('[data-canvas-item]')?.focus()

    // Capture keeps the drag alive when the pointer leaves the slot, which it
    // does constantly at a 19% scale. It can throw if the pointer is already
    // gone; the drag still works without it, so it must not take the handler
    // down with it.
    try {
      target.setPointerCapture(event.pointerId)
    } catch {
      /* dragging degrades to "while the pointer stays over the slot" */
    }

    const onMove = (move: PointerEvent) => {
      // Screen pixels back into background pixels, so what is stored is exactly
      // what the number boxes show.
      const dx = (move.clientX - startX) / scale
      const dy = (move.clientY - startY) / scale

      if (mode === 'resize') {
        const width = startRect.width + dx
        const height = startRect.height + dy

        if (index === 'qr') {
          setQr((current) =>
            current ? resizeQr(current, width, height, size.width, size.height) : current,
          )
        } else {
          setSlots((current) =>
            current.map((slot, i) =>
              i === index ? resizeSlot(slot, width, height, size.width, size.height) : slot,
            ),
          )
        }
        return
      }

      const tolerance = snapTolerance(scale)

      if (index === 'qr') {
        setQr((current) => {
          if (!current) return current
          // The QR box is square, so its rect is width on both axes.
          const snap = snapToCentre(
            origin.x + dx,
            origin.y + dy,
            current.width,
            current.width,
            size.width,
            size.height,
            tolerance,
          )
          setGuides({ x: snap.centredX, y: snap.centredY })
          return {
            ...current,
            x: Math.max(0, Math.min(Math.round(snap.x), size.width - current.width)),
            y: Math.max(0, Math.min(Math.round(snap.y), size.height - current.width)),
          }
        })
        return
      }

      setSlots((current) =>
        current.map((slot, i) => {
          if (i !== index) return slot
          const rect = slotRect(slot)
          const snap = snapToCentre(
            origin.x + dx,
            origin.y + dy,
            rect.width,
            rect.height,
            size.width,
            size.height,
            tolerance,
          )
          setGuides({ x: snap.centredX, y: snap.centredY })
          return moveSlot(slot, snap.x, snap.y, size.width, size.height)
        }),
      )
    }

    const onUp = () => {
      try {
        target.releasePointerCapture(event.pointerId)
      } catch {
        /* already released */
      }
      target.removeEventListener('pointermove', onMove)
      target.removeEventListener('pointerup', onUp)
      target.removeEventListener('pointercancel', onUp)
      // The guides explain a live drag; leaving them up afterwards would read as
      // a property of the slot rather than of the gesture.
      setGuides({ x: false, y: false })
    }

    target.addEventListener('pointermove', onMove)
    target.addEventListener('pointerup', onUp)
    // A cancelled pointer (an OS gesture taking over, the tab losing focus)
    // otherwise leaves the move listener attached and the slot following the
    // cursor with no button held.
    target.addEventListener('pointercancel', onUp)
  }

  /**
   * Nudges the focused slot with the arrow keys — 1 background pixel, or 10
   * with Shift.
   *
   * Dragging is the fast way to place a slot and a hopeless way to land on an
   * exact pixel: at a 19% fit one screen pixel is five background pixels, so a
   * mouse cannot express the difference between 60 and 62. The number boxes can,
   * but going back to the keyboard to type a coordinate for a frame you are
   * looking at is the thing this screen was supposed to stop doing.
   *
   * Steps are absorbed at the edges by the same `moveSlot` clamp the drag uses,
   * so holding an arrow parks a slot flush against the background rather than
   * walking it off.
   */
  const nudge = (event: React.KeyboardEvent, index: number | 'qr') => {
    if (!size) return

    const step = nudgeForKey(event.key, event.shiftKey)
    // Not an arrow: leave it alone, or a focused slot would swallow Tab.
    if (!step) return

    // The canvas would otherwise scroll under the slot being moved.
    event.preventDefault()
    setSelected(index)

    if (index === 'qr') {
      setQr((current) =>
        current
          ? {
              ...current,
              x: Math.max(0, Math.min(current.x + step.dx, size.width - current.width)),
              y: Math.max(0, Math.min(current.y + step.dy, size.height - current.width)),
            }
          : current,
      )
      return
    }

    setSlots((current) =>
      current.map((slot, i) =>
        i === index ? moveSlot(slot, slot.x + step.dx, slot.y + step.dy, size.width, size.height) : slot,
      ),
    )
  }

  // ── saving ─────────────────────────────────────────────────────────────────

  const outOfBounds = size
    ? slots.reduce<number[]>((out, slot, i) => (isSlotWithin(slot, size.width, size.height) ? out : [...out, i]), [])
    : []
  const qrOutOfBounds = Boolean(size && qr && !isQrWithin(qr, size.width, size.height))

  const save = async () => {
    if (!background || !size) {
      toast.error('Pilih gambar latar dulu')
      return
    }
    if (slots.length === 0) {
      toast.error('Tambah minimal satu slot foto')
      return
    }

    setSaving(true)
    try {
      const id = layoutId ?? crypto.randomUUID()
      const draft = { slots: slots.map(toSlotConfig), qr: qr ? toQrConfig(qr) : null }

      const bitmap = await createImageBitmap(background)
      const overlayBitmap = overlay ? await createImageBitmap(overlay) : null
      const previewBlob = await generateLayoutPreview(draft, bitmap, overlayBitmap)
      bitmap.close()
      overlayBitmap?.close()

      const layout: LayoutConfig = {
        id,
        name: name.trim() || undefined,
        // The wire field is kept, misspelling and all, but now holds the file's
        // name rather than a path: the bytes are stored with the layout.
        backgroun_path: backgroundName,
        overlay_path: overlayName || null,
        preview_path: `preview_${id}.png`,
        slots: draft.slots,
        qr: draft.qr,
      }

      // `overlay: null` when there is none is deliberate — it CLEARS a stored
      // one, which is what removing the overlay on an existing layout has to do.
      await saveLayout(layout, { background, overlay, preview: previewBlob })
      toast.success('Layout disimpan')
      navigate('/settings')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Layout gagal disimpan')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="citaria-step-enter flex min-h-0 flex-1 flex-col">
      <header className="flex shrink-0 flex-wrap items-center gap-3 px-5 pb-3 pt-4">
        <Button variant="ghost" onClick={() => navigate('/settings')} className="w-[48px] px-0">
          <ArrowLeft size={20} />
        </Button>
        <h1 className="text-[22px] font-semibold tracking-tight">
          {layoutId ? 'Ubah layout' : 'Buat layout'}
        </h1>

        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Nama layout (opsional)"
          spellCheck={false}
          className="min-h-[40px] min-w-[200px] flex-1 rounded-[10px] bg-raised px-3 text-[14px] text-ink outline-none ring-1 ring-line placeholder:text-ink-faint focus:ring-brand"
        />

        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/settings')}>
            Batal
          </Button>
          <Button onClick={save} disabled={saving || !background || slots.length === 0}>
            {saving ? 'Menyimpan…' : 'Simpan'}
          </Button>
        </div>
      </header>

      {/*
        `grid-rows-[minmax(0,1fr)]` is what keeps the scrollbar in one place.
        An implicit grid row is `auto`, so a column taller than the viewport
        grew the row, the row grew the grid past its `flex-1` height, and the
        page picked up a second scrollbar beside the slot list's own. Letting
        the row shrink below its content confines the overflow to the list.
      */}
      <div className="grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)] gap-4 overflow-hidden px-5 pb-5 lg:grid-cols-[minmax(0,1fr)_380px]">
        {/* ── canvas ─────────────────────────────────────────────────────── */}
        <Surface className="min-h-[320px] overflow-hidden p-3">
          <div ref={viewportRef} className="grid h-full w-full place-items-center overflow-hidden">
            {!background ? (
              <EmptyCanvas onChoose={chooseBackground} />
            ) : (
              <div
                className="relative bg-white/5 shadow-2xl ring-1 ring-line"
                style={{
                  width: (size?.width ?? 0) * scale,
                  height: (size?.height ?? 0) * scale,
                  backgroundImage: `url(${backgroundUrl})`,
                  backgroundSize: '100% 100%',
                }}
                onPointerDown={(event) => {
                  // A click on bare background clears the selection, so the
                  // number boxes stop pointing at something you are not editing.
                  if (event.target === event.currentTarget) setSelected(null)
                }}
              >

                {slots.map((slot, index) => {
                  const box = previewBox(slot, scale)
                  const bad = outOfBounds.includes(index)
                  return (
                    <div
                      key={index}
                      role="button"
                      tabIndex={0}
                      aria-label={`Slot ${index + 1}`}
                      data-canvas-item=""
                      onPointerDown={(event) => startDrag(event, index)}
                      onFocus={() => setSelected(index)}
                      onKeyDown={(event) => nudge(event, index)}
                      className={cn(
                        'absolute grid cursor-move touch-none select-none place-items-center',
                        'overflow-hidden border-2 font-bold leading-none text-ink',
                        // Keyboard users have to see which slot the arrows move.
                        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-hi',
                        selected === index ? 'border-white' : 'border-white/60',
                        bad && 'border-danger',
                      )}
                      style={{
                        left: box.left,
                        top: box.top,
                        width: box.width,
                        height: box.height,
                        // Sized from the box, not the viewport: at a 19% fit a
                        // 160px QR box is 30px on screen, and a viewport-based
                        // clamp spilled the label straight out of it.
                        fontSize: labelSize(box.width, box.height),
                        background: 'var(--color-slot)',
                        outline: selected === index ? '2px solid var(--color-brand-hi)' : undefined,
                        outlineOffset: 2,
                      }}
                    >
                      {index + 1}
                      <ResizeHandle
                        label={`Ubah ukuran slot ${index + 1}`}
                        boxWidth={box.width}
                        boxHeight={box.height}
                        onPointerDown={(event) => startDrag(event, index, 'resize')}
                      />
                    </div>
                  )
                })}

                {/*
                  Centre guides. Only up while a drag is actually snapped, so
                  the line is an explanation of why the box stopped following
                  the cursor rather than permanent chrome.
                */}
                {guides.x && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-brand-hi"
                  />
                )}
                {guides.y && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-brand-hi"
                  />
                )}

                {/*
                  The real stacking: over every photo slot, under the QR. Same
                  order the compositor uses, so the canvas is a preview rather
                  than an approximation.
                  `pointer-events-none` is what makes that safe — the slots
                  underneath stay draggable through it — and the eye toggle is
                  for when an opaque overlay hides the slot you are placing.
                */}
                {overlayUrl && showOverlay && (
                  <img
                    src={overlayUrl}
                    alt=""
                    draggable={false}
                    className="pointer-events-none absolute inset-0 h-full w-full select-none"
                  />
                )}

                {qr && (
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="Slot QR"
                    data-canvas-item=""
                    onPointerDown={(event) => startDrag(event, 'qr')}
                    onFocus={() => setSelected('qr')}
                    onKeyDown={(event) => nudge(event, 'qr')}
                    className={cn(
                      'absolute grid cursor-move touch-none select-none place-items-center',
                      'overflow-hidden border-2 font-bold leading-none text-ink',
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-hi',
                      selected === 'qr' ? 'border-white' : 'border-white/60',
                      qrOutOfBounds && 'border-danger',
                    )}
                    style={{
                      left: qr.x * scale,
                      top: qr.y * scale,
                      width: qr.width * scale,
                      height: qr.width * scale,
                      fontSize: labelSize(qr.width * scale, qr.width * scale),
                      background: 'var(--color-qr)',
                      outline: selected === 'qr' ? '2px solid var(--color-brand-hi)' : undefined,
                      outlineOffset: 2,
                    }}
                  >
                    QR
                    <ResizeHandle
                      label="Ubah ukuran slot QR"
                      boxWidth={qr.width * scale}
                      boxHeight={qr.width * scale}
                      onPointerDown={(event) => startDrag(event, 'qr', 'resize')}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </Surface>

        {/* ── controls ───────────────────────────────────────────────────── */}
        <div className="flex min-h-0 flex-col gap-3 overflow-hidden">
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" className="flex-1" onClick={chooseBackground}>
              <ImageIcon size={16} className="mr-2" />
              {background ? 'Ganti latar' : 'Pilih latar'}
            </Button>
            <Button variant="secondary" className="flex-1" onClick={addSlot}>
              <Plus size={16} className="mr-2" />
              Slot foto
            </Button>
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => {
                setQr((current) => current ?? { ...QR_DEFAULTS })
                setSelected('qr')
              }}
              disabled={qr !== null}
            >
              <QrCode size={16} className="mr-2" />
              Slot QR
            </Button>
          </div>

          {/*
            The overlay is the layer drawn OVER the photos — a frame whose edges
            overlap them, a logo, a foil. The background goes under. Kept as its
            own row rather than a fourth button because it has two extra
            controls of its own.
          */}
          <div className="flex items-center gap-2 rounded-[12px] bg-raised px-3 py-2 ring-1 ring-line">
            <Layers size={16} className="shrink-0 text-ink-muted" />
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] text-ink-muted">Overlay (di atas foto)</span>
              <span
                className={cn('block truncate text-[13px]', overlay ? 'text-ink' : 'text-ink-faint')}
                title={overlayName}
              >
                {overlayName || 'Tidak ada'}
              </span>
            </span>

            {overlay && (
              <button
                type="button"
                aria-label={showOverlay ? 'Sembunyikan overlay' : 'Tampilkan overlay'}
                aria-pressed={showOverlay}
                onClick={() => setShowOverlay((v) => !v)}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-[8px] text-ink-muted ring-1 ring-line"
              >
                {showOverlay ? <Eye size={15} /> : <EyeOff size={15} />}
              </button>
            )}

            <Button variant="secondary" onClick={() => chooseImage('overlay')}>
              {overlay ? 'Ganti' : 'Pilih'}
            </Button>

            {overlay && (
              <button
                type="button"
                aria-label="Hapus overlay"
                onClick={() => {
                  setOverlay(null)
                  setOverlayName('')
                }}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-[8px] text-ink-faint active:bg-danger/15 active:text-danger"
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>

          {size && (
            <>
              <p className="text-[12px] text-ink-faint">
                {backgroundName} · {size.width}×{size.height} px · tampilan {Math.round(scale * 100)}%
              </p>
              {/*
                Said out loud because it is not discoverable: at this zoom a
                one-pixel nudge is a sub-pixel move on screen, so nobody finds it
                by accident — and it is the only way to hit an exact coordinate
                without going back to the number boxes.
              */}
              <p className="text-[12px] text-ink-faint">
                Geser slot dengan tarik, atau klik lalu <kbd className="rounded bg-raised px-1">←↑↓→</kbd>{' '}
                untuk geser 1 px · <kbd className="rounded bg-raised px-1">Shift</kbd> + panah = 10 px
              </p>
            </>
          )}

          {(outOfBounds.length > 0 || qrOutOfBounds) && (
            <p className="flex items-start gap-2 rounded-[10px] bg-danger/10 p-3 text-[13px] text-danger ring-1 ring-danger/30">
              <AlertTriangle size={16} className="mt-[2px] shrink-0" />
              <span>
                {[
                  outOfBounds.length > 0 && `Slot ${outOfBounds.map((i) => i + 1).join(', ')}`,
                  qrOutOfBounds && 'Slot QR',
                ]
                  .filter(Boolean)
                  .join(' dan ')}{' '}
                keluar dari gambar latar dan tidak akan ikut tercetak.
              </span>
            </p>
          )}

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
            {slots.length === 0 && (
              <p className="rounded-[12px] border border-dashed border-line px-4 py-8 text-center text-[13px] text-ink-muted">
                Belum ada slot. Tambah minimal satu agar foto punya tempat.
              </p>
            )}

            {slots.map((slot, index) => (
              <SlotEditor
                key={index}
                index={index}
                total={slots.length}
                slot={slot}
                selected={selected === index}
                outOfBounds={outOfBounds.includes(index)}
                onSelect={() => setSelected(index)}
                onPatch={(patch, cause) => patchSlot(index, patch, cause)}
                onReorder={(direction) => reorderSlot(index, direction)}
                onRemove={() => removeSlot(index)}
              />
            ))}

            {qr && (
              <fieldset
                onFocus={() => setSelected('qr')}
                className={cn(
                  'rounded-[12px] border p-3',
                  selected === 'qr' ? 'border-brand bg-raised' : 'border-line',
                )}
              >
                <legend className="px-1 text-[12px] font-bold">Slot QR</legend>
                <div className="grid grid-cols-3 gap-2">
                  <NumberField label="X" value={qr.x} onChange={(x) => setQr({ ...qr, x })} />
                  <NumberField label="Y" value={qr.y} onChange={(y) => setQr({ ...qr, y })} />
                  <NumberField
                    label="Ukuran"
                    value={qr.width}
                    onChange={(width) => setQr({ ...qr, width })}
                  />
                </div>
                <Button
                  variant="secondary"
                  className="mt-2 w-full"
                  onClick={() => {
                    setQr(null)
                    setSelected(null)
                  }}
                >
                  <Trash2 size={14} className="mr-2" />
                  Hapus slot QR
                </Button>
              </fieldset>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function EmptyCanvas({ onChoose }: { onChoose: () => void }) {
  return (
    <div className="grid place-items-center px-6 text-center">
      <ImageIcon size={40} className="mb-4 text-ink-faint" />
      <p className="text-[17px] font-medium">Pilih gambar latar</p>
      <p className="mt-1 max-w-[380px] text-[14px] text-ink-muted">
        Bingkai cetak 4R. Semua posisi slot diukur dalam piksel gambar ini, jadi pilih dulu
        sebelum menata slot.
      </p>
      <Button className="mt-5" onClick={onChoose}>
        <ImageIcon size={18} className="mr-2" />
        Pilih gambar
      </Button>
    </div>
  )
}

/**
 * One slot's controls.
 *
 * The number boxes are the desktop app's and behave identically — the height is
 * read-only and derived, and the width box means "the short side of the ratio",
 * with the Landscape swap applied on save. What is added is the position in the
 * order, which decides which photo lands here.
 */
function SlotEditor({
  index,
  total,
  slot,
  selected,
  outOfBounds,
  onSelect,
  onPatch,
  onReorder,
  onRemove,
}: {
  index: number
  total: number
  slot: SlotDraft
  selected: boolean
  outOfBounds: boolean
  onSelect: () => void
  onPatch: (patch: Partial<SlotDraft>, cause: 'width' | 'other') => void
  onReorder: (direction: -1 | 1) => void
  onRemove: () => void
}) {
  const rect = slotRect(slot)

  return (
    <fieldset
      onFocus={onSelect}
      onClick={onSelect}
      className={cn(
        'rounded-[12px] border p-3 transition-colors',
        selected ? 'border-brand bg-raised' : 'border-line',
        outOfBounds && 'border-danger',
      )}
    >
      <legend className="flex items-center gap-2 px-1 text-[12px] font-bold">
        <span className="grid h-5 w-5 place-items-center rounded-full bg-brand text-[11px] text-on-brand">
          {index + 1}
        </span>
        Foto ke-{index + 1}
      </legend>

      <div className="mb-2 flex items-center gap-1">
        <button
          type="button"
          aria-label={`Naikkan slot ${index + 1}`}
          onClick={() => onReorder(-1)}
          disabled={index === 0}
          className="grid h-8 w-8 place-items-center rounded-[8px] text-ink-muted ring-1 ring-line disabled:opacity-30"
        >
          <ChevronUp size={15} />
        </button>
        <button
          type="button"
          aria-label={`Turunkan slot ${index + 1}`}
          onClick={() => onReorder(1)}
          disabled={index === total - 1}
          className="grid h-8 w-8 place-items-center rounded-[8px] text-ink-muted ring-1 ring-line disabled:opacity-30"
        >
          <ChevronDown size={15} />
        </button>
        <span className="ml-1 text-[11px] text-ink-faint">
          {rect.width}×{rect.height} px
        </span>
        <button
          type="button"
          aria-label={`Hapus slot ${index + 1}`}
          onClick={onRemove}
          className="ml-auto grid h-8 w-8 place-items-center rounded-[8px] text-ink-faint active:bg-danger/15 active:text-danger"
        >
          <Trash2 size={15} />
        </button>
      </div>

      <Choice
        label="Orientasi"
        name={`slot-${index}-orientation`}
        options={['Portrait', 'Landscape'] as SlotOrientation[]}
        value={slot.orientation}
        onChange={(orientation) => onPatch({ orientation }, 'other')}
      />

      {/* Indonesian in the original UI; kept. */}
      <Choice
        label="Rasio Foto"
        name={`slot-${index}-ratio`}
        options={['2:3', '1:1'] as SlotRatio[]}
        value={slot.ratio}
        onChange={(ratio) => onPatch({ ratio }, 'other')}
      />

      <div className="grid grid-cols-4 gap-2">
        <NumberField label="X" value={slot.x} onChange={(x) => onPatch({ x }, 'other')} />
        <NumberField label="Y" value={slot.y} onChange={(y) => onPatch({ y }, 'other')} />
        <NumberField
          label="Lebar"
          value={slot.width}
          onChange={(width) => onPatch({ width }, 'width')}
        />
        <NumberField label="Tinggi" value={slot.height} readOnly />
      </div>
    </fieldset>
  )
}

function Choice<T extends string>({
  label,
  name,
  options,
  value,
  onChange,
}: {
  label: string
  name: string
  options: T[]
  value: T
  onChange: (value: T) => void
}) {
  return (
    <div className="mb-2">
      <p className="mb-1 text-[11px] text-ink-muted">{label}</p>
      <div className="flex gap-1">
        {options.map((option) => (
          <label
            key={option}
            className={cn(
              'min-h-[32px] flex-1 cursor-pointer rounded-[8px] px-2 text-center text-[12px] leading-[32px] ring-1',
              value === option ? 'bg-brand text-on-brand ring-brand' : 'text-ink-muted ring-line',
            )}
          >
            <input
              type="radio"
              name={name}
              className="sr-only"
              checked={value === option}
              onChange={() => onChange(option)}
            />
            {option}
          </label>
        ))}
      </div>
    </div>
  )
}

function NumberField({
  label,
  value,
  onChange,
  readOnly,
}: {
  label: string
  value: number
  onChange?: (value: number) => void
  readOnly?: boolean
}) {
  return (
    <label className="flex flex-col gap-1 text-[11px]" title={label}>
      <span className="truncate text-ink-muted">{label}</span>
      <input
        type="number"
        value={value}
        readOnly={readOnly}
        onChange={(event) => {
          const next = Number.parseInt(event.target.value, 10)
          // The original called Int32.Parse unguarded and threw on an empty box.
          onChange?.(Number.isFinite(next) ? next : 0)
        }}
        className={cn(
          'h-8 rounded-[8px] px-2 text-ink outline-none ring-1 ring-line focus:ring-brand',
          readOnly ? 'bg-line text-ink-muted' : 'bg-raised',
        )}
      />
    </label>
  )
}

/**
 * Label size for a box on the canvas.
 *
 * Proportional to the box rather than the viewport. The first version used a
 * `clamp(…, 2vw, …)`, which is fine at a 50% scale and spills the text clean out
 * of the box once a 4R background is fitted at 19% — a 160px QR slot is 30px on
 * screen there.
 */
function labelSize(width: number, height: number): number {
  return Math.max(9, Math.min(30, Math.min(width, height) * 0.42))
}

/**
 * The corner grip that resizes a slot.
 *
 * Only the WIDTH can be set — the ratio derives the height (§5) — so this is an
 * aspect-locked handle, not a free one. Dragging either axis proposes a width
 * and `resizeSlot` picks the larger, which is why pulling down on a Landscape
 * slot widens it: down is its width box.
 *
 * Sized in SCREEN pixels rather than background pixels so it stays grabbable at
 * a 19% fit, where a 480px slot is 90px on screen.
 */
function ResizeHandle({
  label,
  boxWidth,
  boxHeight,
  onPointerDown,
}: {
  label: string
  boxWidth: number
  boxHeight: number
  onPointerDown: (event: React.PointerEvent) => void
}) {
  // Capped at 14px so it stays grabbable, floored at 8px, but never more than a
  // third of the box — a fixed grip swallowed a 30px QR slot at a 19% fit.
  const size = Math.max(8, Math.min(14, Math.min(boxWidth, boxHeight) / 3))

  return (
    <span
      role="button"
      tabIndex={-1}
      aria-label={label}
      onPointerDown={onPointerDown}
      style={{ width: size, height: size }}
      className={cn(
        'absolute bottom-0 right-0 cursor-nwse-resize touch-none',
        'rounded-tl-[4px] border-l-2 border-t-2 border-white bg-black/45',
      )}
    />
  )
}
